import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { borrowBook, BORROWING_CONFIG } from "@/lib/borrowing";
import { useAuth } from "@/hooks/useAuth";
import { TISACard } from "@/components/tisa/TISACard";
import { TISAButton } from "@/components/tisa/TISAButton";
import { IB_PYP_THEMES } from "@/types/database";
import {
  BookOpen,
  Coins,
  Calendar,
  AlertCircle,
  CheckCircle,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";

interface BorrowBookModalProps {
  isOpen: boolean;
  onClose: () => void;
  book: {
    id: string;
    title: string;
    author: string;
    theme_color: keyof typeof IB_PYP_THEMES;
    age_group: string;
    copies?: Array<{ id: string; status: string | null }>;
  };
  availableCopyId: string;
}

export function BorrowBookModal({
  isOpen,
  onClose,
  book,
  availableCopyId,
}: BorrowBookModalProps) {
  const { user, refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const [isConfirming, setIsConfirming] = useState(false);

  const borrowMutation = useMutation({
    mutationFn: borrowBook,
    onSuccess: (result) => {
      const dueDate = result.dueDate.toLocaleDateString();

      // Immediately refresh user data to reflect coin/books changes
      refreshUser();

      // Force refresh all queries with refetchType: 'all' to bypass cache
      queryClient.invalidateQueries({
        queryKey: ["user-loans", user?.id],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["user-coins", user?.id],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["coin-transactions", user?.id],
        refetchType: "all",
      });
      queryClient.invalidateQueries({ queryKey: ["books"] });
      queryClient.invalidateQueries({ queryKey: ["book", book.id] });
      queryClient.invalidateQueries({ queryKey: ["recommended-books"] });

      toast({
        title: "Book borrowed successfully! 📚",
        description: `"${book.title}" is now yours! Due back on ${dueDate}. ${result.coinCost} TISAcoins spent.`,
      });

      onClose();
      setIsConfirming(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Couldn't borrow book 😕",
        description: error.message,
        variant: "destructive",
      });
      setIsConfirming(false);
    },
  });

  if (!isOpen || !user) return null;

  const theme = IB_PYP_THEMES[book.theme_color];
  const dueDate = new Date();
  dueDate.setDate(dueDate.getDate() + BORROWING_CONFIG.defaultLoanDurationDays);

  const hasEnoughCoins = (user.coins ?? 0) >= BORROWING_CONFIG.coinCosts.borrow;

  const handleBorrow = async () => {
    if (!user) return;

    setIsConfirming(true);

    try {
      await borrowMutation.mutateAsync({
        copyId: availableCopyId,
        userId: user.id,
      });
    } catch (error) {
      // Error handled in onError callback
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <TISACard
        size="lg"
        className="w-full max-w-md relative"
        themeColor={book.theme_color}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-muted hover:bg-muted/80 transition-colors"
          disabled={borrowMutation.isPending}
        >
          <X className="h-4 w-4" />
        </button>

        <div className="space-y-6">
          {/* Header */}
          <div className="text-center space-y-3">
            <div className="text-4xl">{theme.emoji}</div>
            <h2 className="text-child-xl font-bold">Borrow This Book?</h2>
            <p className="text-child-sm text-muted-foreground">
              Check out "{book.title}" and start your reading adventure!
            </p>
          </div>

          {/* Book Info */}
          <TISACard variant="outline" size="sm">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <BookOpen className="h-5 w-5 mt-1 flex-shrink-0" />
                <div className="min-w-0">
                  <h3 className="font-semibold text-child-base leading-tight">
                    {book.title}
                  </h3>
                  <p className="text-child-sm text-muted-foreground">
                    by {book.author}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <div
                      className={cn(
                        `theme-${book.theme_color} px-2 py-1 rounded text-child-xs font-medium`,
                      )}
                    >
                      {theme.name}
                    </div>
                    <span className="text-child-xs text-muted-foreground">
                      Ages {book.age_group}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </TISACard>

          {/* Borrowing Details */}
          <div className="space-y-4">
            <h3 className="font-semibold text-child-base">
              Borrowing Details:
            </h3>

            <div className="space-y-3">
              {/* Cost */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Coins className="h-4 w-4 text-tisa-yellow-600" />
                  <span className="text-child-sm font-medium">Cost</span>
                </div>
                <div
                  className={cn(
                    "font-semibold",
                    hasEnoughCoins ? "text-green-600" : "text-destructive",
                  )}
                >
                  {BORROWING_CONFIG.coinCosts.borrow} 🪙
                </div>
              </div>

              {/* Due Date */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-tisa-blue-600" />
                  <span className="text-child-sm font-medium">Due Date</span>
                </div>
                <div className="font-semibold">
                  {dueDate.toLocaleDateString()}
                </div>
              </div>

              {/* Current Balance */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 rounded-full bg-tisa-yellow-600"></div>
                  <span className="text-child-sm font-medium">
                    Your Balance
                  </span>
                </div>
                <div className="font-semibold">{user.coins ?? 0} 🪙</div>
              </div>
            </div>
          </div>

          {/* Warning or Success Message */}
          {!hasEnoughCoins ? (
            <TISACard
              variant="outline"
              className="border-destructive bg-destructive/5"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-medium text-child-sm text-destructive">
                    Not enough TISAcoins!
                  </p>
                  <p className="text-child-xs text-muted-foreground">
                    You need {BORROWING_CONFIG.coinCosts.borrow} TISAcoins to
                    borrow this book. Return books on time to earn more coins!
                  </p>
                </div>
              </div>
            </TISACard>
          ) : (
            <TISACard
              variant="outline"
              className="border-green-200 bg-green-50"
            >
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-medium text-child-sm text-green-800">
                    Ready to borrow!
                  </p>
                  <p className="text-child-xs text-green-700">
                    Return on time to earn{" "}
                    {BORROWING_CONFIG.coinRewards.returnOnTime} TISAcoins! 🎉
                  </p>
                </div>
              </div>
            </TISACard>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <TISAButton
              onClick={onClose}
              variant="outline"
              size="lg"
              className="flex-1"
              disabled={borrowMutation.isPending}
            >
              Cancel
            </TISAButton>

            <TISAButton
              onClick={handleBorrow}
              variant="theme-green"
              size="lg"
              className="flex-1"
              disabled={!hasEnoughCoins || borrowMutation.isPending}
              isLoading={borrowMutation.isPending}
              icon={
                !borrowMutation.isPending ? (
                  <BookOpen className="h-4 w-4" />
                ) : undefined
              }
            >
              {borrowMutation.isPending
                ? isConfirming
                  ? "Borrowing..."
                  : "Processing..."
                : "Borrow Book! 📚"}
            </TISAButton>
          </div>

          {/* Terms */}
          <p className="text-child-xs text-center text-muted-foreground leading-relaxed">
            By borrowing this book, you agree to return it by the due date in
            good condition. Late returns may result in reduced coin rewards.
          </p>
        </div>
      </TISACard>
    </div>
  );
}
